import {DataStatus, Segment} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, mergeAll} from 'ramda';

import {useGetPostponeJobQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {useDataGridTotalRows} from '../../../hooks/useDataGridTotalRows';
import {DmsDataGrid} from '../../DmsDataGrid/DmsDataGrid';
import {JobForm, JobFormProps} from '../../JobForm/JobForm';

const POSTPONE_JOB_ITEMS_GRID_CODE = 'postpone-job-items';

const PostponedJobSegments = {
  WORK_ITEMS: 'workItems',
  DETAILS: 'details',
} as const;

type PostponedJobSegments = (typeof PostponedJobSegments)[keyof typeof PostponedJobSegments];

interface PostponedJobDetailProps extends TestIdProps {
  postponeJobId: string;
  onSubmit: JobFormProps['onSubmit'];
}

export function PostponedJobDetail(props: PostponedJobDetailProps) {
  const [segment, setSegment] = useState<PostponedJobSegments>(PostponedJobSegments.WORK_ITEMS);

  const {data, isLoading, isError} = useGetPostponeJobQuery({postponeJobId: props.postponeJobId});

  const itemsCount = useDataGridTotalRows(POSTPONE_JOB_ITEMS_GRID_CODE, {
    postponeJobId: props.postponeJobId,
  });

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {postponeJobId: props.postponeJobId}]);

  return (
    <VStack spacing={4}>
      <Segment<PostponedJobSegments>
        minWidth={35}
        value={segment}
        onChange={setSegment}
        options={[
          {
            value: PostponedJobSegments.WORK_ITEMS,
            label: `${i18n.t('entity.orderRequest.labels.workItems')} (${itemsCount ?? 0})`,
          },
          {
            value: PostponedJobSegments.DETAILS,
            label: i18n.t('entity.workshop.labels.requestDetails'),
          },
        ]}
        data-testid={suffixTestId('segment', props)}
      />
      <Show when={segment === PostponedJobSegments.WORK_ITEMS}>
        <DmsDataGrid
          autoHeight
          gridCode={POSTPONE_JOB_ITEMS_GRID_CODE}
          queryModifier={queryModifier}
          data-testid={suffixTestId('workItems', props)}
        />
      </Show>
      <Show when={segment === PostponedJobSegments.DETAILS}>
        <DataStatus isLoading={isLoading} isError={isError} isEmpty={isNil(data)} minHeight={75}>
          <JobForm
            jobData={data}
            onSubmit={props.onSubmit}
            data-testid={suffixTestId('jobForm', props)}
          />
        </DataStatus>
      </Show>
    </VStack>
  );
}
